import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { GlossScrollDirective } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.directive";

export interface StyleInfoBlock {
  title: string;
  titleColor: string;
  bullets: string[];
}

export interface DedupStyleInfoDialogData {
  selectedStyle: string;
  styleInfoMap?: Record<string, StyleInfoBlock>;
}

const DEFAULT_STYLE_INFO: Record<string, StyleInfoBlock> = {
  A: {
    title: "Style-A",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Except for dateCreated, dateModified and vaultId we compare everything for duplication.",
      "If you think you should have had some duplicates here because your transactions were the same, it might not be seen as duplicates because one or both of sourceId and/or id properties is/are different.",
      "If you see duplicates here that means except for dateCreated, dateModified and vaultId everything is the same, which means you can go ahead and delete the duplicates.",
    ],
  },
  B: {
    title: "Style-B",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Except for dateCreated, dateModified, vaultId and sourceId, we compare everything for duplicate check.",
      "If you see duplicates here but not in Style-A, it is because the sourceId is different. Meaning we created the same transaction twice and when we did we created and linked a new source transaction, so each transaction despite being the same, they have different sourceIds.",
      "This is safe to delete because the id of the transactions are being used in the duplicate check and if you see duplicates here they have the same ids. Good to delete.",
    ],
  },
  C: {
    title: "Style-C",
    titleColor: "tw-text-orange-600",
    bullets: [
      "Except for dateCreated, dateModified, vaultId and id we compare everything to check on duplication.",
      "If you see any duplicates here, that means a single source transaction is pointing to multiple transactions. This is a rare and weird state. Please do not delete and inform authorities :)",
    ],
  },
  ID: {
    title: "Style-ID",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Grouping uses only the transaction id field.",
      "Rows that share the same id appear as one duplicate group no matter how different the rest of the payload is.",
      "Deleting duplicates keeps one row per id: prefers a valid source link (srId) and a real category over Uncategorized, then newest by date created — same rule used after login silent cleanup alongside Style-A.",
    ],
  },
};

@Component({
  selector: "app-vault-dedup-style-info-dialog",
  standalone: true,
  imports: [GlossScrollDirective, MatDialogModule, MatButtonModule],
  templateUrl: "./vault-dedup-style-info-dialog.component.html",
})
export class VaultDedupStyleInfoDialogComponent {
  readonly styleInfo: StyleInfoBlock;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DedupStyleInfoDialogData) {
    const map = data.styleInfoMap ?? DEFAULT_STYLE_INFO;
    this.styleInfo = map[data.selectedStyle] ?? Object.values(map)[0];
  }
}
